import React, { useState } from 'react';
import { Box, HStack, Input, Text, Button, Dialog, Portal, CloseButton } from '@chakra-ui/react';
import { FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const WHATSAPP_GREEN = '#25D366';

// Format raw input into "+48 123 123 123": leading "+", 2-digit country code, then groups of 3.
const formatPhone = (raw) => {
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) return '';
    const cc = digits.slice(0, 2);
    const rest = digits.slice(2);
    const groups = rest.match(/.{1,3}/g) || [];
    return ('+' + [cc, ...groups].join(' ')).trimEnd();
};

// Strip mask formatting to the "+XXXXXXXXXXX" form the backend expects.
const normalizePhone = (masked) => {
    const digits = masked.replace(/[^\d]/g, '');
    return digits ? `+${digits}` : '';
};

const WhatsAppConnect = ({ linkWhatsApp, conversationId, colors }) => {
    const { t } = useTranslation('chat');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isLinking, setIsLinking] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLinked, setIsLinked] = useState(false);

    const handlePhoneChange = (e) => {
        setPhoneNumber(formatPhone(e.target.value));
    };

    const linkToWhatsAppButton = (phone) => {
        setIsLinking(true);
        linkWhatsApp(normalizePhone(phone), conversationId)
            .then(() => {
                setIsLinked(true);
                setIsDialogOpen(false);
            })
            .finally(() => setIsLinking(false))
            .catch(err => console.error('Failed to link WhatsApp', err));
    };

    return (
        <>
            <Box
                as="button"
                onClick={() => !isLinked && setIsDialogOpen(true)}
                disabled={isLinked}
                display="flex"
                alignItems="center"
                gap={{ base: 0, md: 2 }}
                px={{ base: 2, md: 3 }}
                py="6px"
                borderRadius="10px"
                borderWidth="1px"
                borderColor={WHATSAPP_GREEN}
                bg={WHATSAPP_GREEN}
                color="white"
                fontSize="sm"
                fontWeight="500"
                lineHeight="1"
                whiteSpace="nowrap"
                flexShrink={0}
                cursor={isLinked ? 'default' : 'pointer'}
                _hover={isLinked ? {} : { bg: '#1EBE5D' }}
                transition="background 0.15s"
                aria-label={isLinked ? t('whatsapp.connected') : t('whatsapp.openButton')}
                title={isLinked ? t('whatsapp.connected') : t('whatsapp.openButton')}
            >
                {/* Full label on desktop, icon-only on mobile to save header space */}
                <Box as="span" display={{ base: 'none', md: 'inline' }}>
                    {isLinked ? t('whatsapp.connected') : t('whatsapp.openButton')}
                </Box>
                <FaWhatsapp size={16} />
            </Box>

            <Dialog.Root open={isDialogOpen} onOpenChange={(e) => setIsDialogOpen(e.open)}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner p={4}>
                        <Dialog.Content
                            bg={colors.chatBg}
                            color={colors.textPrimary}
                            borderColor={colors.borderColor}
                            w={{ base: 'calc(100vw - 2rem)', md: 'md' }}
                            maxW="100%"
                            maxH="calc(100dvh - 2rem)"
                            overflowY="auto"
                        >
                            <Dialog.Body pt={6} pb={4} px={6}>
                                <HStack gap={2} mb={4}>
                                    <FaWhatsapp size={20} color={WHATSAPP_GREEN} />
                                    <Text fontWeight="semibold" fontSize="lg" color={colors.textPrimary}>
                                        {t('whatsapp.dialogTitle')}
                                    </Text>
                                </HStack>
                                <Text mb={3} fontSize="sm" color={colors.textSecondary} lineHeight="1.5">
                                    {t('whatsapp.intro')}
                                </Text>
                                <Text mb={2} fontSize="sm" color={colors.textSecondary} lineHeight="1.5">
                                    {t('whatsapp.phoneHint')}
                                </Text>
                                <Input
                                    placeholder={t('whatsapp.phonePlaceholder')}
                                    value={phoneNumber}
                                    onChange={handlePhoneChange}
                                    onKeyDown={e => e.key === 'Enter' && linkToWhatsAppButton(phoneNumber)}
                                    inputMode="tel"
                                    bg={colors.inputBg}
                                    color={colors.textPrimary}
                                    borderColor={colors.borderColor}
                                    _placeholder={{ color: colors.dateText }}
                                />
                                <HStack mt={4} justifyContent="flex-end">
                                    <Button
                                        bg={WHATSAPP_GREEN}
                                        color="white"
                                        borderRadius="10px"
                                        px={6}
                                        fontWeight="600"
                                        loading={isLinking}
                                        onClick={() => linkToWhatsAppButton(phoneNumber)}
                                        _hover={{ bg: '#1EBE5D' }}
                                        _active={{ bg: '#1AA34F' }}
                                    >
                                        {t('whatsapp.connectButton')}
                                    </Button>
                                </HStack>
                            </Dialog.Body>
                            <Dialog.CloseTrigger asChild>
                                <CloseButton size="sm" position="absolute" top="2" right="2" color={colors.iconColor} />
                            </Dialog.CloseTrigger>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
        </>
    );
};

export default WhatsAppConnect;
